/* ==========================================================================
   Pool Busters — Pool Closings & Winterization
   Estimator engine, chapter router and page interactions.

   Contains NO prices. Every dollar figure is read at runtime from
   assets/pb-pool-closing-pricing.json. Money is handled in integer cents so
   $12.50 add-ons never drift. No external libraries: the QR code is encoded
   here rather than fetched from an image service, so the code always matches
   whatever booking URL the theme editor is set to.
   ========================================================================== */
/* Progressive-enhancement marker: this line only runs if the whole file
   parsed. Until it exists the stylesheet keeps every reveal visible and every
   FAQ answer open, so a truncated asset leaves a readable page rather than a
   blank one. */
document.documentElement.classList.add('pbcl-js');
(function () {
  'use strict';

  /* The asset snippet is rendered by every section, so guard against the
     script being evaluated more than once on a page. */
  if (typeof window !== 'undefined') {
    if (window.__PBCL_LOADED__) return;
    window.__PBCL_LOADED__ = true;
  }

  var ROOT_SEL = '[data-pbcl-root]';
  var SS_KEY = 'pbcl.closing.v1';

  /* ---------------------------------------------------------------- utils */
  function $(s, c) { return (c || document).querySelector(s); }
  function $$(s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); }
  function el(tag, cls, txt) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (txt != null) n.textContent = txt;
    return n;
  }
  function cents(n) { return Math.round(Number(n) * 100); }
  var fmt;
  try { fmt = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }); }
  catch (e) { fmt = { format: function (v) { return '$' + v.toFixed(2); } }; }
  function money(c) { return fmt.format(c / 100); }
  function reduceMotion() {
    return typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
  function store(obj) {
    try { sessionStorage.setItem(SS_KEY, JSON.stringify(obj)); } catch (e) {}
  }
  function restore() {
    try { return JSON.parse(sessionStorage.getItem(SS_KEY) || 'null'); } catch (e) { return null; }
  }

  /* ==================================================================
     QR — byte mode, error correction level M, versions 1 to 12.

     Written out rather than pulled from a CDN for three reasons: the page
     must not depend on a third party staying up, the booking URL is a theme
     setting so the code has to be generated from whatever it currently says,
     and an image-generator service would leak the URL to someone else's
     server on every page view.
     ================================================================== */
  var QR = (function () {
    /* [data codewords, EC codewords per block, [blocks, data per block] ...] */
    var LEVEL_M = {
      1:  [16,  10, [[1, 16]]],
      2:  [28,  16, [[1, 28]]],
      3:  [44,  26, [[1, 44]]],
      4:  [64,  18, [[2, 32]]],
      5:  [86,  24, [[2, 43]]],
      6:  [108, 16, [[4, 27]]],
      7:  [124, 18, [[4, 31]]],
      8:  [154, 22, [[2, 38], [2, 39]]],
      9:  [182, 22, [[3, 36], [2, 37]]],
      10: [216, 26, [[4, 43], [1, 44]]],
      11: [254, 30, [[1, 50], [4, 51]]],
      12: [290, 22, [[6, 36], [2, 37]]]
    };
    var ALIGN = {
      1: [], 2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30], 6: [6, 34],
      7: [6, 22, 38], 8: [6, 24, 42], 9: [6, 26, 46], 10: [6, 28, 50],
      11: [6, 30, 54], 12: [6, 32, 58]
    };

    /* GF(256) with primitive polynomial 0x11D */
    var EXP = new Array(512), LOG = new Array(256);
    (function () {
      var x = 1;
      for (var i = 0; i < 255; i++) {
        EXP[i] = x; LOG[x] = i;
        x <<= 1;
        if (x & 0x100) x ^= 0x11D;
      }
      for (var j = 255; j < 512; j++) EXP[j] = EXP[j - 255];
    })();
    function gmul(a, b) { return (a === 0 || b === 0) ? 0 : EXP[LOG[a] + LOG[b]]; }

    function genPoly(n) {
      var g = [1];
      for (var i = 0; i < n; i++) {
        var ng = new Array(g.length + 1);
        for (var k = 0; k < ng.length; k++) ng[k] = 0;
        for (var j = 0; j < g.length; j++) {
          ng[j] ^= g[j];
          ng[j + 1] ^= gmul(g[j], EXP[i]);
        }
        g = ng;
      }
      return g;
    }

    function ecc(data, n) {
      var g = genPoly(n);
      var rem = data.slice().concat(new Array(n).fill(0));
      for (var i = 0; i < data.length; i++) {
        var f = rem[i];
        if (!f) continue;
        for (var j = 0; j < g.length; j++) rem[i + j] ^= gmul(g[j], f);
      }
      return rem.slice(data.length);
    }

    function utf8(str) {
      var out = [], i, c;
      var s = encodeURIComponent(str);
      for (i = 0; i < s.length; i++) {
        c = s.charAt(i);
        if (c === '%') { out.push(parseInt(s.substr(i + 1, 2), 16)); i += 2; }
        else out.push(s.charCodeAt(i));
      }
      return out;
    }

    function bitsFor(version, bytes) {
      /* mode indicator 4 + character count indicator + 8 per byte */
      var cci = version < 10 ? 8 : 16;
      return 4 + cci + bytes * 8;
    }

    function pickVersion(len) {
      for (var v = 1; v <= 12; v++) {
        if (bitsFor(v, len) <= LEVEL_M[v][0] * 8) return v;
      }
      return 0;
    }

    function encodeData(version, bytes) {
      var cap = LEVEL_M[version][0] * 8;
      var bits = [];
      function push(val, n) { for (var i = n - 1; i >= 0; i--) bits.push((val >> i) & 1); }
      push(4, 4);                                   /* byte mode */
      push(bytes.length, version < 10 ? 8 : 16);    /* character count */
      for (var i = 0; i < bytes.length; i++) push(bytes[i], 8);
      /* terminator, then pad to a byte boundary, then alternating pad bytes */
      var term = Math.min(4, cap - bits.length);
      for (var t = 0; t < term; t++) bits.push(0);
      while (bits.length % 8) bits.push(0);
      var out = [];
      for (var b = 0; b < bits.length; b += 8) {
        var v = 0;
        for (var k = 0; k < 8; k++) v = (v << 1) | bits[b + k];
        out.push(v);
      }
      var pads = [0xEC, 0x11], p = 0;
      while (out.length < LEVEL_M[version][0]) out.push(pads[p++ % 2]);
      return out;
    }

    function interleave(version, data) {
      var spec = LEVEL_M[version], ecLen = spec[1], groups = spec[2];
      var blocks = [], at = 0, g, i;
      for (g = 0; g < groups.length; g++) {
        for (i = 0; i < groups[g][0]; i++) {
          var d = data.slice(at, at + groups[g][1]);
          at += groups[g][1];
          blocks.push({ d: d, e: ecc(d, ecLen) });
        }
      }
      var maxD = 0;
      for (i = 0; i < blocks.length; i++) maxD = Math.max(maxD, blocks[i].d.length);
      var out = [], j;
      for (i = 0; i < maxD; i++)
        for (j = 0; j < blocks.length; j++)
          if (i < blocks[j].d.length) out.push(blocks[j].d[i]);
      for (i = 0; i < ecLen; i++)
        for (j = 0; j < blocks.length; j++) out.push(blocks[j].e[i]);
      return out;
    }

    function bch(value, generator, genBits) {
      var v = value << (genBits - 1);
      var gBit = 1;
      while ((generator >> gBit) !== 0) gBit++;
      while (true) {
        var vBit = 0;
        while ((v >> vBit) !== 0) vBit++;
        if (vBit < gBit) break;
        v ^= generator << (vBit - gBit);
      }
      return v;
    }

    function build(version, mask, codewords) {
      var size = version * 4 + 17;
      var m = [], reserved = [], y, x;
      for (y = 0; y < size; y++) {
        m.push(new Array(size).fill(0));
        reserved.push(new Array(size).fill(false));
      }
      function set(px, py, v) { m[py][px] = v ? 1 : 0; reserved[py][px] = true; }

      /* finders + separators */
      function finder(ox, oy) {
        for (var dy = -1; dy <= 7; dy++) {
          for (var dx = -1; dx <= 7; dx++) {
            var px = ox + dx, py = oy + dy;
            if (px < 0 || py < 0 || px >= size || py >= size) continue;
            var inRing = (dx >= 0 && dx <= 6 && dy >= 0 && dy <= 6);
            var on = inRing && (dx === 0 || dx === 6 || dy === 0 || dy === 6 ||
                                (dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4));
            set(px, py, on);
          }
        }
      }
      finder(0, 0); finder(size - 7, 0); finder(0, size - 7);

      /* timing */
      for (var i = 8; i < size - 8; i++) {
        set(i, 6, i % 2 === 0);
        set(6, i, i % 2 === 0);
      }

      /* alignment */
      var centres = ALIGN[version];
      for (var a = 0; a < centres.length; a++) {
        for (var b = 0; b < centres.length; b++) {
          var cx = centres[a], cy = centres[b];
          if ((cx === 6 && cy === 6) || (cx === 6 && cy === size - 7) ||
              (cx === size - 7 && cy === 6)) continue;
          for (var dy2 = -2; dy2 <= 2; dy2++)
            for (var dx2 = -2; dx2 <= 2; dx2++)
              set(cx + dx2, cy + dy2,
                  Math.max(Math.abs(dx2), Math.abs(dy2)) !== 1);
        }
      }

      /* dark module + format areas reserved */
      set(8, size - 8, 1);
      for (var f = 0; f < 9; f++) {
        if (f !== 6) { reserved[8][f] = true; reserved[f][8] = true; }
      }
      for (var f2 = 0; f2 < 8; f2++) {
        reserved[8][size - 1 - f2] = true;
        reserved[size - 1 - f2][8] = true;
      }

      /* version information, versions 7 and up */
      if (version >= 7) {
        var vinfo = (version << 12) | bch(version, 0x1F25, 13);
        for (var vi = 0; vi < 18; vi++) {
          var bit = (vinfo >> vi) & 1;
          var r = Math.floor(vi / 3), c = vi % 3;
          set(r, size - 11 + c, bit);
          set(size - 11 + c, r, bit);
        }
      }

      /* data, upward-then-downward two-module columns, skipping column 6 */
      var bitIdx = 0, dir = -1, row = size - 1;
      for (var col = size - 1; col > 0; col -= 2) {
        if (col === 6) col--;
        while (true) {
          for (var s = 0; s < 2; s++) {
            var cx2 = col - s;
            if (!reserved[row][cx2]) {
              var v = 0;
              if (bitIdx < codewords.length * 8) {
                v = (codewords[bitIdx >> 3] >> (7 - (bitIdx & 7))) & 1;
              }
              bitIdx++;
              var maskOn;
              switch (mask) {
                case 0: maskOn = (row + cx2) % 2 === 0; break;
                case 1: maskOn = row % 2 === 0; break;
                case 2: maskOn = cx2 % 3 === 0; break;
                case 3: maskOn = (row + cx2) % 3 === 0; break;
                case 4: maskOn = (Math.floor(row / 2) + Math.floor(cx2 / 3)) % 2 === 0; break;
                case 5: maskOn = ((row * cx2) % 2) + ((row * cx2) % 3) === 0; break;
                case 6: maskOn = ((((row * cx2) % 2) + ((row * cx2) % 3)) % 2) === 0; break;
                default: maskOn = ((((row + cx2) % 2) + ((row * cx2) % 3)) % 2) === 0;
              }
              m[row][cx2] = maskOn ? v ^ 1 : v;
            }
          }
          row += dir;
          if (row < 0 || row >= size) { row -= dir; dir = -dir; break; }
        }
      }

      /* format information, level M = 0b00 */
      var fmtBits = ((0 << 3) | mask);
      var finfo = (((fmtBits << 10) | bch(fmtBits, 0x537, 11)) ^ 0x5412);
      for (var k = 0; k < 15; k++) {
        var fb = (finfo >> k) & 1;
        /* vertical copy, column 8: bit 0 at the top, skipping the timing row */
        if (k < 6) m[k][8] = fb;
        else if (k < 8) m[k + 1][8] = fb;
        else m[size - 15 + k][8] = fb;
        /* horizontal copy, row 8: bit 0 at the right-hand end */
        if (k < 8) m[8][size - 1 - k] = fb;
        else if (k === 8) m[8][7] = fb;
        else m[8][14 - k] = fb;
      }
      m[size - 8][8] = 1;   /* the always-dark module */
      return m;
    }

    function penalty(m) {
      var size = m.length, score = 0, i, j, run, last, dark = 0;
      /* rule 1 — runs of five or more */
      for (i = 0; i < size; i++) {
        run = 1; last = m[i][0];
        for (j = 1; j < size; j++) {
          if (m[i][j] === last) { run++; } else { if (run >= 5) score += 3 + (run - 5); run = 1; last = m[i][j]; }
        }
        if (run >= 5) score += 3 + (run - 5);
        run = 1; last = m[0][i];
        for (j = 1; j < size; j++) {
          if (m[j][i] === last) { run++; } else { if (run >= 5) score += 3 + (run - 5); run = 1; last = m[j][i]; }
        }
        if (run >= 5) score += 3 + (run - 5);
      }
      /* rule 2 — 2x2 blocks */
      for (i = 0; i < size - 1; i++)
        for (j = 0; j < size - 1; j++)
          if (m[i][j] === m[i][j + 1] && m[i][j] === m[i + 1][j] && m[i][j] === m[i + 1][j + 1]) score += 3;
      /* rule 3 — finder-like patterns */
      var P1 = [1, 0, 1, 1, 1, 0, 1, 0, 0, 0, 0];
      var P2 = [0, 0, 0, 0, 1, 0, 1, 1, 1, 0, 1];
      function match(get, at) {
        var a = true, b = true;
        for (var k = 0; k < 11; k++) {
          var v = get(at + k);
          if (v !== P1[k]) a = false;
          if (v !== P2[k]) b = false;
        }
        return a || b;
      }
      for (i = 0; i < size; i++) {
        for (j = 0; j <= size - 11; j++) {
          if (match(function (k) { return m[i][k]; }, j)) score += 40;
          if (match(function (k) { return m[k][i]; }, j)) score += 40;
        }
      }
      /* rule 4 — proportion of dark modules */
      for (i = 0; i < size; i++) for (j = 0; j < size; j++) dark += m[i][j];
      var pct = (dark * 100) / (size * size);
      score += Math.floor(Math.abs(pct - 50) / 5) * 10;
      return score;
    }

    /* Returns a matrix of 0/1, or null when the text will not fit. */
    function matrix(text) {
      var bytes = utf8(String(text));
      var version = pickVersion(bytes.length);
      if (!version) return null;
      var codewords = interleave(version, encodeData(version, bytes));
      var best = null, bestScore = Infinity;
      for (var mask = 0; mask < 8; mask++) {
        var m = build(version, mask, codewords);
        var p = penalty(m);
        if (p < bestScore) { bestScore = p; best = m; }
      }
      return best;
    }

    /* One <path> of black squares on a white ground: sharp at any size, and
       small enough to sit inline in the document. */
    function svg(text, opts) {
      var m = matrix(text);
      if (!m) return null;
      opts = opts || {};
      var quiet = opts.quiet == null ? 4 : opts.quiet;
      var size = m.length, dim = size + quiet * 2, d = '', y, x;
      for (y = 0; y < size; y++) {
        for (x = 0; x < size; x++) {
          if (m[y][x]) d += 'M' + (x + quiet) + ' ' + (y + quiet) + 'h1v1h-1z';
        }
      }
      var ns = 'http://www.w3.org/2000/svg';
      var root = document.createElementNS(ns, 'svg');
      root.setAttribute('viewBox', '0 0 ' + dim + ' ' + dim);
      root.setAttribute('role', 'img');
      root.setAttribute('aria-label', opts.label || 'QR code');
      var bg = document.createElementNS(ns, 'rect');
      bg.setAttribute('width', dim); bg.setAttribute('height', dim); bg.setAttribute('fill', '#ffffff');
      root.appendChild(bg);
      var path = document.createElementNS(ns, 'path');
      path.setAttribute('d', d); path.setAttribute('fill', '#070B11');
      root.appendChild(path);
      return root;
    }

    return { matrix: matrix, svg: svg };
  })();

  /* ==================================================================
     ESTIMATOR
     Every figure comes from the pricing file. Nothing is rounded, bundled
     or combined: the customer sees the base closing, each upgrade and each
     product on its own line, then one total underneath.
     ================================================================== */
  function Estimator(root, DB) {
    this.root = root;
    this.DB = DB;
    this.i = 0;
    var saved = restore();
    this.state = {
      area: null, community: '', cover: null, level: 'standard',
      finalVac: false, filter: 'none', addons: {}, kit: 'none'
    };
    if (saved && typeof saved === 'object') {
      for (var k in this.state) if (Object.prototype.hasOwnProperty.call(saved, k)) this.state[k] = saved[k];
      if (!this.state.addons || typeof this.state.addons !== 'object') this.state.addons = {};
    }
    this.steps = this.buildSteps();
    this.render();
    this.updateRail();
  }

  Estimator.prototype.save = function () { store(this.state); };
  Estimator.prototype.area = function () {
    var a = this.DB.areas, i;
    for (i = 0; i < a.length; i++) if (a[i].id === this.state.area) return a[i];
    return null;
  };
  Estimator.prototype.cover = function () {
    var c = this.DB.covers, i;
    for (i = 0; i < c.length; i++) if (c[i].id === this.state.cover) return c[i];
    return null;
  };
  Estimator.prototype.level = function () {
    var l = this.DB.levels, i;
    for (i = 0; i < l.length; i++) if (l[i].id === this.state.level) return l[i];
    return l[0];
  };
  Estimator.prototype.addonById = function (id) {
    var all = this.DB.addons.concat(this.DB.products || []), i;
    for (i = 0; i < all.length; i++) if (all[i].id === id) return all[i];
    return null;
  };
  /* An add-on's stored value is a count for quantity add-ons and true/false
     for the rest. One reader so the two can never be confused. */
  Estimator.prototype.qtyOf = function (a) {
    var v = this.state.addons[a.id];
    if (a.qty) return clamp(parseInt(v, 10) || 0, 0, a.max || 99);
    return v ? 1 : 0;
  };
  Estimator.prototype.setQty = function (a, n) {
    if (a.qty) {
      n = clamp(parseInt(n, 10) || 0, 0, a.max || 99);
      if (n) this.state.addons[a.id] = n; else delete this.state.addons[a.id];
    } else {
      if (n) this.state.addons[a.id] = true; else delete this.state.addons[a.id];
    }
    this.save();
  };
  /* Base price for the chosen area and cover. null means "contact for quote" —
     which is a real answer, not a missing one, and must never render as $0. */
  Estimator.prototype.basePrice = function () {
    var a = this.area();
    if (!a || !this.state.cover) return undefined;      /* not answered yet */
    if (a.quote || !a.prices) return null;              /* quoted individually */
    var p = a.prices[this.state.cover];
    return (typeof p === 'number') ? p : null;
  };

  /* ---------------------------------------------------------------- quote */
  Estimator.prototype.quote = function () {
    var S = this.state, DB = this.DB, self = this;
    var groups = [], i;

    function group(key, label, note) {
      var g = { key: key, label: label, note: note || '', lines: [], sub: 0 };
      groups.push(g);
      return g;
    }
    function line(g, t, v, opts) {
      opts = opts || {};
      g.lines.push({ t: t, v: v, quote: !!opts.quote, meta: opts.meta || '' });
      if (typeof v === 'number') g.sub += v;
    }

    var base = this.basePrice();
    var a = this.area(), c = this.cover();
    var gBase = group('base', 'Base closing price');
    if (a && c) {
      if (base === null) {
        line(gBase, a.name + ' — ' + c.label + ' closing', null, { quote: true });
        gBase.sub = null;
        gBase.note = a.quote_note || '';
      } else {
        line(gBase, a.name + ' — ' + c.label + ' closing', base);
      }
    }

    if (S.level === 'premium') {
      var lv = this.level();
      var gp = group('premium', 'Premium Closing upgrade');
      line(gp, lv.label + ' — added to the Standard Closing price', lv.price);
    }

    if (S.finalVac) {
      var cl = DB.cleaning[0];
      var gc = group('cleaning', 'Cleaning upgrades');
      line(gc, cl.label, cl.price, { meta: cl.detail });
    }

    var gEq = group('equipment', 'Equipment and feature winterization');
    for (i = 0; i < DB.addons.length; i++) {
      var ad = DB.addons[i], n = this.qtyOf(ad);
      if (!n) continue;
      line(gEq, ad.label + (ad.qty ? ' × ' + n : ''), ad.price * n,
           { meta: ad.qty ? money(ad.price) + ' per ' + ad.unit : '' });
    }
    if (!gEq.lines.length) groups.splice(groups.indexOf(gEq), 1);

    var f = null;
    for (i = 0; i < DB.filter.length; i++) if (DB.filter[i].id === S.filter) f = DB.filter[i];
    if (f && f.price) {
      var gf = group('filter', 'Filter cleaning');
      line(gf, f.label, f.price, { meta: f.detail });
    }

    var kit = null;
    for (i = 0; i < DB.kits.length; i++) if (DB.kits[i].id === S.kit) kit = DB.kits[i];
    if (kit && kit.price) {
      var gk = group('chem', 'Chemical products');
      line(gk, kit.label, kit.price);
    }

    var gOther = group('other', 'Other selected products');
    var prods = DB.products || [];
    for (i = 0; i < prods.length; i++) {
      var pr = prods[i], pn = this.qtyOf(pr);
      if (!pn) continue;
      line(gOther, pr.label + (pr.qty ? ' × ' + pn : ''), pr.price * pn,
           { meta: pr.qty ? money(pr.price) + ' per ' + pr.unit : '' });
    }
    if (!gOther.lines.length) groups.splice(groups.indexOf(gOther), 1);

    var addTotal = 0;
    for (i = 0; i < groups.length; i++) {
      if (groups[i].key === 'base') continue;
      addTotal += groups[i].sub || 0;
    }
    var total = (typeof base === 'number') ? base + addTotal : null;
    return {
      groups: groups,
      base: base,
      addTotal: addTotal,
      total: total,
      quoteOnly: base === null,
      answered: !!(a && c)
    };
  };

  /* Plain-text recap, used by the booking panel and carried into the contact
     form so nobody has to retype their selections. */
  Estimator.prototype.summary = function () {
    var q = this.quote(), out = [], i, j;
    var a = this.area(), c = this.cover();
    if (!q.answered) return null;
    out.push('POOL CLOSING ESTIMATE — ' + (this.DB.season || '') + ' season');
    if (this.state.community) out.push('Community: ' + this.state.community);
    out.push('Service area: ' + (a ? a.name : ''));
    out.push('Cover: ' + (c ? c.label : ''));
    out.push('Closing: ' + this.level().label);
    out.push('');
    for (i = 0; i < q.groups.length; i++) {
      var g = q.groups[i];
      out.push(g.label.toUpperCase());
      for (j = 0; j < g.lines.length; j++) {
        var ln = g.lines[j];
        out.push('  ' + ln.t + '  ' + (ln.v === null ? 'Contact for quote' : money(ln.v)));
      }
    }
    out.push('');
    out.push('Estimated total: ' + (q.total === null ? 'Contact for quote' : money(q.total)));
    if (q.total === null && q.addTotal) {
      out.push('Selected extras: ' + money(q.addTotal) + ' (base closing quoted separately)');
    }
    out.push('');
    out.push('An estimate only. Booking and availability go through the Pool Busters client portal.');
    return out.join('\n');
  };

  /* Short version for the booking panel's "what you selected" list. */
  Estimator.prototype.recap = function () {
    var q = this.quote();
    if (!q.answered) return null;
    var a = this.area(), c = this.cover(), rows = [], i, j;
    rows.push(['Area', a.name + (this.state.community ? ' — ' + this.state.community : '')]);
    rows.push(['Cover', c.label]);
    rows.push(['Closing', this.level().label]);
    var extras = [];
    for (i = 0; i < q.groups.length; i++) {
      if (q.groups[i].key === 'base' || q.groups[i].key === 'premium') continue;
      for (j = 0; j < q.groups[i].lines.length; j++) extras.push(q.groups[i].lines[j].t);
    }
    rows.push(['Selected', extras.length ? extras.join(', ') : 'Nothing beyond the closing itself']);
    rows.push(['Estimate', q.total === null ? 'Contact for quote' : money(q.total)]);
    return rows;
  };

  /* ------------------------------------------------------- field builders */
  Estimator.prototype.radios = function (key, label, opts, cls, onChange) {
    var C = this, wrap = el('div');
    if (label) wrap.appendChild(el('p', 'pbcl-lbl', label));
    var g = el('div', 'pbcl-opts ' + (cls || ''));
    g.setAttribute('role', 'radiogroup');
    if (label) g.setAttribute('aria-label', label);
    opts.forEach(function (o, idx) {
      var id = 'pbcl-' + key + '-' + idx;
      var lab = el('label', 'pbcl-opt');
      var inp = el('input');
      inp.type = 'radio'; inp.name = 'pbcl-' + key; inp.id = id; inp.value = o.v;
      if (C.state[key] === o.v) inp.checked = true;
      var face = el('span', 'pbcl-opt__face');
      face.appendChild(el('span', 'pbcl-opt__t', o.t));
      if (o.d) face.appendChild(el('span', 'pbcl-opt__d', o.d));
      if (o.p != null) face.appendChild(el('span', 'pbcl-opt__p', o.p));
      inp.addEventListener('change', function () {
        C.state[key] = o.v;
        C.save();
        C.updateRail();
        if (onChange) onChange();
      });
      lab.appendChild(inp); lab.appendChild(face);
      g.appendChild(lab);
    });
    wrap.appendChild(g);
    return wrap;
  };

  Estimator.prototype.addonRow = function (a) {
    var C = this;
    var li = el('li', 'pbcl-add');
    var id = 'pbcl-add-' + a.id;
    var box = el('input', 'pbcl-add__box');
    box.type = 'checkbox'; box.id = id;
    box.checked = C.qtyOf(a) > 0;

    var lab = el('label', 'pbcl-add__lb');
    lab.setAttribute('for', id);
    lab.appendChild(el('span', 'pbcl-add__t', a.label));
    if (a.detail) lab.appendChild(el('span', 'pbcl-add__d', a.detail));

    var price = el('span', 'pbcl-add__p', money(a.price));
    if (a.qty && a.unit) price.appendChild(el('span', 'u', 'per ' + a.unit));

    li.appendChild(box); li.appendChild(lab); li.appendChild(price);

    var qtyWrap = null;
    if (a.qty) {
      qtyWrap = el('div', 'pbcl-add__qty');
      var stepper = el('div', 'pbcl-qty');
      var minus = el('button', null, '−');
      minus.type = 'button';
      minus.setAttribute('aria-label', 'One fewer ' + a.unit + ' — ' + a.label);
      var num = el('input');
      num.type = 'number'; num.min = '1'; num.max = String(a.max || 99); num.step = '1';
      num.id = id + '-qty';
      num.value = String(Math.max(1, C.qtyOf(a)));
      num.setAttribute('aria-label', 'Number of ' + a.unit + 's — ' + a.label);
      var plus = el('button', null, '+');
      plus.type = 'button';
      plus.setAttribute('aria-label', 'One more ' + a.unit + ' — ' + a.label);
      var sum = el('span', 'pbcl-add__sum');

      function paint() {
        var n = C.qtyOf(a);
        sum.textContent = n ? money(a.price * n) + ' total' : '';
        minus.disabled = (parseInt(num.value, 10) || 1) <= 1;
        plus.disabled = (parseInt(num.value, 10) || 1) >= (a.max || 99);
      }
      function commit(n) {
        n = clamp(n, 1, a.max || 99);
        num.value = String(n);
        if (box.checked) C.setQty(a, n);
        paint(); C.updateRail();
      }
      minus.addEventListener('click', function () { commit((parseInt(num.value, 10) || 1) - 1); });
      plus.addEventListener('click', function () { commit((parseInt(num.value, 10) || 1) + 1); });
      num.addEventListener('change', function () { commit(parseInt(num.value, 10) || 1); });

      stepper.appendChild(minus); stepper.appendChild(num); stepper.appendChild(plus);
      qtyWrap.appendChild(el('span', 'lab', 'How many ' + a.unit + 's?'));
      qtyWrap.appendChild(stepper);
      qtyWrap.appendChild(sum);
      qtyWrap.hidden = !box.checked;
      li.appendChild(qtyWrap);
      paint();
    }

    box.addEventListener('change', function () {
      if (a.qty) {
        var n = box.checked ? Math.max(1, parseInt($('#' + id + '-qty', li).value, 10) || 1) : 0;
        C.setQty(a, n);
        qtyWrap.hidden = !box.checked;
      } else {
        C.setQty(a, box.checked ? 1 : 0);
      }
      C.updateRail();
      if (qtyWrap) {
        var s = $('.pbcl-add__sum', qtyWrap);
        var n2 = C.qtyOf(a);
        s.textContent = n2 ? money(a.price * n2) + ' total' : '';
      }
    });
    return li;
  };

  /* ------------------------------------------------------ step definitions */
  Estimator.prototype.buildSteps = function () {
    var C = this, DB = this.DB;
    return [
      {
        key: 'area', nav: 'Area', legend: 'Where is your pool?',
        help: 'Pick your community and the estimator uses the matching service-area price. If yours is not on the list, choose the last option and we will confirm your area with you.',
        build: function (body) {
          var f = el('div', 'pbcl-field');
          var lb = el('label', null, 'Community'); lb.setAttribute('for', 'pbcl-community');
          var sel = el('select', 'pbcl-select'); sel.id = 'pbcl-community';
          var ph = el('option', null, 'Choose your community…'); ph.value = '';
          sel.appendChild(ph);
          DB.areas.forEach(function (a) {
            if (!a.communities.length) {
              var o = el('option', null, a.name); o.value = a.id + '|';
              if (C.state.area === a.id && !C.state.community) o.selected = true;
              sel.appendChild(o);
              return;
            }
            var og = el('optgroup'); og.label = a.name;
            a.communities.forEach(function (cm) {
              var o = el('option', null, cm);
              o.value = a.id + '|' + cm;
              if (C.state.area === a.id && C.state.community === cm) o.selected = true;
              og.appendChild(o);
            });
            sel.appendChild(og);
          });
          sel.addEventListener('change', function () {
            var parts = sel.value.split('|');
            C.state.area = parts[0] || null;
            C.state.community = parts[1] || '';
            C.save(); C.updateRail(); C.render();
            document.dispatchEvent(new CustomEvent('pbcl:area', { detail: C.state.area }));
          });
          f.appendChild(lb); f.appendChild(sel);
          body.appendChild(f);

          var a = C.area();
          if (a) {
            var n = el('div', 'pbcl-note' + (a.quote ? ' pbcl-note--warn' : ''));
            if (a.quote) {
              n.innerHTML = '<strong>' + a.name + '</strong> — closings here are priced individually. ' +
                'Keep going: your selections are all kept, and the booking step sends them to us so we can confirm your price.';
            } else {
              n.innerHTML = '<strong>' + a.name + '</strong> — safety cover ' + money(a.prices.safety) +
                ', traditional or beaded cover ' + money(a.prices.traditional) +
                ', no cover ' + money(a.prices.none) + '.';
            }
            body.appendChild(n);
          }
        },
        valid: function () { return C.state.area ? null : 'Choose your community before continuing.'; }
      },
      {
        key: 'cover', nav: 'Cover', legend: 'What goes over the pool for winter?',
        help: 'This is the single biggest factor in the closing price. A no-cover closing is priced at the same rate as a traditional or beaded cover closing.',
        build: function (body) {
          var a = C.area();
          var opts = DB.covers.map(function (cv) {
            var p = '';
            if (a && a.prices) p = money(a.prices[cv.id]);
            else if (a && a.quote) p = 'Contact for quote';
            return { v: cv.id, t: cv.label, d: cv.help, p: p || null };
          });
          body.appendChild(C.radios('cover', null, opts, 'pbcl-opts--3', function () { C.render(); }));
          var n = el('div', 'pbcl-note');
          n.innerHTML = 'A <strong>no-cover closing</strong> still receives the full applicable plumbing and equipment winterization. Nothing is installed over the pool.';
          body.appendChild(n);
        },
        valid: function () { return C.state.cover ? null : 'Tell us what covers the pool so we can price the closing.'; }
      },
      {
        key: 'level', nav: 'Closing', legend: 'Standard or Premium Closing?',
        help: 'Standard is the complete applicable winterization checklist. Premium adds discharge vacuuming and autumn leaf removal for a flat $100 on top of the Standard price.',
        build: function (body) {
          var opts = DB.levels.map(function (lv) {
            return {
              v: lv.id, t: lv.label, d: lv.summary,
              p: lv.price ? '+ ' + money(lv.price) : 'Included in the closing price'
            };
          });
          body.appendChild(C.radios('level', null, opts, 'pbcl-opts--2'));
          var n = el('div', 'pbcl-note pbcl-note--warn');
          n.innerHTML = '<strong>Standard Closing customers clean the pool before the appointment.</strong> ' +
            'Cleaning and debris removal are not part of the Standard Closing unless you add a cleaning upgrade on the next step.';
          body.appendChild(n);
        }
      },
      {
        key: 'cleaning', nav: 'Cleaning', legend: 'Would you like us to clean as well?',
        help: 'These are separate from one another. The Premium Closing upgrade and the Final vacuum and skim are never combined into one charge.',
        build: function (body) {
          var cl = DB.cleaning[0];
          var ul = el('ul', 'pbcl-adds');
          var li = el('li', 'pbcl-add');
          var box = el('input', 'pbcl-add__box');
          box.type = 'checkbox'; box.id = 'pbcl-finalvac'; box.checked = !!C.state.finalVac;
          var lab = el('label', 'pbcl-add__lb'); lab.setAttribute('for', 'pbcl-finalvac');
          lab.appendChild(el('span', 'pbcl-add__t', cl.label));
          lab.appendChild(el('span', 'pbcl-add__d', cl.detail));
          box.addEventListener('change', function () {
            C.state.finalVac = box.checked; C.save(); C.updateRail();
          });
          li.appendChild(box); li.appendChild(lab);
          li.appendChild(el('span', 'pbcl-add__p', money(cl.price)));
          ul.appendChild(li);
          body.appendChild(ul);

          var n = el('div', 'pbcl-note');
          if (C.state.level === 'premium') {
            n.innerHTML = 'You already have the <strong>Premium Closing</strong>, which covers discharge vacuuming and autumn leaf removal. The final vacuum and skim is a separate 40-minute clean and stays its own line if you add it.';
          } else {
            n.innerHTML = 'The <strong>Premium Closing</strong> upgrade on the previous step is the other cleaning option. It covers discharge vacuuming and autumn leaves; this one is up to 40 minutes of pool cleaning at the appointment.';
          }
          body.appendChild(n);
        }
      },
      {
        key: 'filter', nav: 'Filter', legend: 'Filter cleaning',
        help: 'Two genuinely different services. On-site cleaning happens during the closing; the overnight clean means your cartridges leave with us and come back after a chemical soak.',
        build: function (body) {
          var opts = DB.filter.map(function (f) {
            return { v: f.id, t: f.label, d: f.detail, p: f.price ? money(f.price) : null };
          });
          body.appendChild(C.radios('filter', null, opts, 'pbcl-opts--3'));
        }
      },
      {
        key: 'addons', nav: 'Add-ons', legend: 'Equipment and winterization add-ons',
        help: 'Only the ones that apply to your setup. Everything here is optional, and everything is priced on its own line.',
        build: function (body) {
          var ul = el('ul', 'pbcl-adds');
          DB.addons.forEach(function (a) { ul.appendChild(C.addonRow(a)); });
          (DB.products || []).forEach(function (p) { ul.appendChild(C.addonRow(p)); });
          body.appendChild(ul);
          var n = el('div', 'pbcl-note');
          n.innerHTML = 'Pool antifreeze is <strong>optional</strong> — it applies to some plumbing setups and not others. If you are not sure, leave it off and ask the technician at the appointment.';
          body.appendChild(n);
        }
      },
      {
        key: 'kit', nav: 'Chemicals', legend: 'Closing chemical kit',
        help: 'A closing chemical kit is not included in the base closing price. If you add one, your technician applies the products during the appointment.',
        build: function (body) {
          var opts = DB.kits.map(function (k) {
            var d = k.includes && k.includes.length ? k.includes.join(' · ') : k.detail;
            return { v: k.id, t: k.label, d: d, p: k.price ? money(k.price) : null };
          });
          body.appendChild(C.radios('kit', null, opts, 'pbcl-opts--3'));
          var n = el('div', 'pbcl-note pbcl-note--warn');
          n.innerHTML = '<strong>Not included in the base closing.</strong> Choosing “No chemical kit” is a complete, valid estimate — plenty of customers use their own products.';
          body.appendChild(n);
        }
      },
      {
        key: 'result', nav: 'Estimate', legend: 'Your closing estimate',
        help: null,
        build: function (body) { C.renderResult(body); }
      }
    ];
  };

  /* ---------------------------------------------------------------- render */
  Estimator.prototype.render = function () {
    var C = this, steps = this.steps, step = steps[this.i];

    var bar = $('[data-pbcl-bar] i', this.root);
    if (bar) bar.style.width = Math.round((this.i / (steps.length - 1)) * 100) + '%';
    var cnt = $('[data-pbcl-count]', this.root);
    if (cnt) cnt.textContent = this.i === steps.length - 1
      ? 'Complete' : 'Step ' + (this.i + 1) + ' of ' + (steps.length - 1);
    var ttl = $('[data-pbcl-stepname]', this.root);
    if (ttl) ttl.textContent = step.nav;

    var dots = $('[data-pbcl-dots]', this.root);
    if (dots) {
      dots.innerHTML = '';
      steps.forEach(function (s, k) {
        var b = el('button', null, s.nav);
        b.type = 'button';
        if (k === C.i) b.setAttribute('aria-current', 'step');
        if (k < C.i) b.setAttribute('data-done', '1');
        if (k > C.i) b.disabled = true;
        b.addEventListener('click', function () { C.go(k); });
        dots.appendChild(b);
      });
    }

    var panel = $('[data-pbcl-panel]', this.root);
    if (!panel) return;
    panel.innerHTML = '';

    var fs = el('fieldset', 'pbcl-fs');
    var lg = el('legend', null, step.legend);
    fs.appendChild(lg);
    if (step.help) fs.appendChild(el('p', 'pbcl-fs__help', step.help));
    var body = el('div');
    fs.appendChild(body);
    step.build.call(this, body);
    panel.appendChild(fs);

    var err = el('div', 'pbcl-err');
    err.setAttribute('role', 'alert');
    err.hidden = true;
    panel.appendChild(err);

    var nav = el('div', 'pbcl-nav');
    if (this.i > 0) {
      var back = el('button', 'pbcl-btn pbcl-btn--ghost pbcl-btn--sm', 'Back');
      back.type = 'button';
      back.addEventListener('click', function () { C.go(C.i - 1); });
      nav.appendChild(back);
    }
    nav.appendChild(el('span', 'pbcl-spacer'));

    if (this.i < steps.length - 1) {
      var next = el('button', 'pbcl-btn', this.i === steps.length - 2 ? 'See my estimate' : 'Continue');
      next.type = 'button';
      next.addEventListener('click', function () {
        var msg = step.valid ? step.valid() : null;
        if (msg) {
          err.textContent = msg; err.hidden = false;
          err.scrollIntoView({ block: 'nearest', behavior: reduceMotion() ? 'auto' : 'smooth' });
          return;
        }
        err.hidden = true;
        C.go(C.i + 1);
      });
      nav.appendChild(next);
    } else {
      var again = el('button', 'pbcl-btn pbcl-btn--ghost pbcl-btn--sm', 'Start again');
      again.type = 'button';
      again.addEventListener('click', function () {
        C.state = { area: null, community: '', cover: null, level: 'standard',
                    finalVac: false, filter: 'none', addons: {}, kit: 'none' };
        C.save(); C.go(0); C.updateRail();
      });
      nav.appendChild(again);
      var book = el('button', 'pbcl-btn', 'Take this to booking');
      book.type = 'button';
      book.setAttribute('data-pbcl-jump', 'pool-closing-book');
      book.addEventListener('click', function () {
        var t = document.getElementById('pool-closing-book');
        if (window.PBCL_ROUTER && window.PBCL_ROUTER.go('pool-closing-book')) return;
        if (t) t.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
      });
      nav.appendChild(book);
    }
    panel.appendChild(nav);
  };

  Estimator.prototype.go = function (i) {
    this.i = clamp(i, 0, this.steps.length - 1);
    this.render();
    this.updateRail();
    var stage = $('[data-pbcl-stage]', this.root);
    if (stage && !reduceMotion()) {
      var top = stage.getBoundingClientRect().top + window.pageYOffset - 88;
      if (window.pageYOffset > top) window.scrollTo({ top: top, behavior: 'smooth' });
    }
  };

  Estimator.prototype.renderResult = function (body) {
    var q = this.quote(), C = this;

    q.groups.forEach(function (g) {
      var blk = el('div', 'pbcl-qblock');
      var k = el('p', 'pbcl-qblock__k');
      k.appendChild(el('span', null, g.label));
      blk.appendChild(k);
      var ul = el('ul', 'pbcl-qblock__l');
      g.lines.forEach(function (ln) {
        var li = el('li');
        var t = el('span', 't', ln.t);
        if (ln.meta) {
          var m = el('small');
          m.style.display = 'block';
          m.style.opacity = '.8';
          m.textContent = ln.meta;
          t.appendChild(m);
        }
        li.appendChild(t);
        var v = el('span', 'v', ln.v === null ? 'Contact for quote' : money(ln.v));
        if (ln.v === null) v.setAttribute('data-quote', '1');
        li.appendChild(v);
        ul.appendChild(li);
      });
      blk.appendChild(ul);
      if (g.lines.length > 1 && g.sub !== null) {
        var sub = el('div', 'pbcl-qblock__sub');
        sub.appendChild(el('span', null, g.label + ' subtotal'));
        sub.appendChild(el('b', null, money(g.sub)));
        blk.appendChild(sub);
      }
      if (g.note) {
        var nt = el('p', 'pbcl-small');
        nt.style.marginTop = '.9rem';
        nt.textContent = g.note;
        blk.appendChild(nt);
      }
      body.appendChild(blk);
    });

    var tot = el('div', 'pbcl-qblock pbcl-qblock--total');
    var tk = el('p', 'pbcl-qblock__k');
    tk.appendChild(el('span', null, q.quoteOnly ? 'Your selections' : 'Estimated total'));
    tot.appendChild(tk);
    var fig = el('span', 'fig', q.total === null ? 'Contact for quote' : money(q.total));
    if (q.total === null) fig.setAttribute('data-quote', '1');
    tot.appendChild(fig);
    if (q.total === null) {
      var p1 = el('p', 'pbcl-small');
      p1.style.marginTop = '.9rem';
      p1.textContent = q.addTotal
        ? 'The upgrades and products you selected come to ' + money(q.addTotal) +
          '. The base closing for your area is quoted individually — send us these selections and we will confirm the full price.'
        : 'The base closing for your area is quoted individually. Send us your selections and we will confirm the price.';
      tot.appendChild(p1);
    } else {
      var p2 = el('p', 'pbcl-small');
      p2.style.marginTop = '.9rem';
      p2.textContent = 'An estimate, not an invoice. Taxes are applied at invoicing, and your appointment is confirmed through the Pool Busters client portal.';
      tot.appendChild(p2);
    }
    body.appendChild(tot);

    var note = el('div', 'pbcl-note');
    note.innerHTML = 'Services are completed <strong>as applicable to your specific pool and equipment setup</strong>. ' +
      'If something on the checklist is not present on your pool, it simply is not done — it is not swapped for something else.';
    body.appendChild(note);

    /* Hand the summary to the booking section and to the contact form. */
    var text = this.summary();
    $$('[data-pbcl-quotefield]').forEach(function (f) { if (text) f.value = text; });
    document.dispatchEvent(new CustomEvent('pbcl:quote', { detail: { recap: C.recap(), text: text } }));
  };

  Estimator.prototype.updateRail = function () {
    var q = this.quote(), C = this;
    var a = this.area(), c = this.cover();

    $$('[data-pbcl-rail-total]').forEach(function (n) {
      if (!q.answered) { n.textContent = '—'; n.removeAttribute('data-quote'); return; }
      if (q.total === null) { n.textContent = 'Contact for quote'; n.setAttribute('data-quote', '1'); }
      else { n.textContent = money(q.total); n.removeAttribute('data-quote'); }
    });
    $$('[data-pbcl-rail-sub]').forEach(function (n) {
      n.textContent = q.answered
        ? 'Estimate only — booking is confirmed in the client portal.'
        : 'Your estimate appears as you answer.';
    });
    $$('[data-pbcl-rail-empty]').forEach(function (n) { n.hidden = q.answered; });

    $$('[data-pbcl-rail-list]').forEach(function (ul) {
      ul.innerHTML = '';
      if (!q.answered) return;
      function row(k, v) {
        var li = el('li');
        li.appendChild(el('span', 'k', k));
        li.appendChild(el('span', 'v', v));
        ul.appendChild(li);
      }
      row(a.name.replace(/ —.*$/, ''), c.short);
      row(C.level().label, q.base === null ? 'By quote' : money(q.base + (C.state.level === 'premium' ? C.level().price : 0)));
      q.groups.forEach(function (g) {
        if (g.key === 'base' || g.key === 'premium') return;
        row(g.label, money(g.sub));
      });
    });

    var text = this.summary();
    $$('[data-pbcl-quotefield]').forEach(function (f) {
      if (text) f.value = text;
    });
    document.dispatchEvent(new CustomEvent('pbcl:quote', { detail: { recap: this.recap(), text: text } }));

    var sheet = $('[data-pbcl-sheet]');
    if (sheet) sheet.setAttribute('data-live', q.answered ? '1' : '0');
  };

  /* ==================================================================
     PAGE INTERACTIONS
     ================================================================== */
  function initReveals(scope) {
    var els = $$('.pbcl-rv', scope);
    if (!('IntersectionObserver' in window) || reduceMotion()) {
      els.forEach(function (e) { e.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.06 });
    els.forEach(function (e) { io.observe(e); });
  }

  /* The checklist explorer: a tab list on the left, one panel on the right.
     Arrow keys move between tabs (WAI-ARIA tabs pattern). Without JavaScript
     every panel is shown in order — see the CSS no-JS floor. */
  function initExplorers(scope) {
    $$('[data-pbcl-explorer]', scope).forEach(function (ex) {
      var tabs = $$('[role="tab"]', ex);
      var panels = $$('[role="tabpanel"]', ex);
      if (!tabs.length || tabs.length !== panels.length) return;
      function select(i, focus) {
        tabs.forEach(function (t, k) {
          var on = k === i;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          panels[k].hidden = !on;
        });
        if (focus) tabs[i].focus();
      }
      tabs.forEach(function (t, i) {
        t.addEventListener('click', function () { select(i); });
        t.addEventListener('keydown', function (e) {
          var n = tabs.length, j = i;
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') j = (i + 1) % n;
          else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') j = (i - 1 + n) % n;
          else if (e.key === 'Home') j = 0;
          else if (e.key === 'End') j = n - 1;
          else return;
          e.preventDefault(); select(j, true);
        });
      });
    });
  }

  function initAccordions(scope) {
    $$('.pbcl-acc__q', scope).forEach(function (btn) {
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;
      panel.style.height = '0px';
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        if (open) {
          panel.style.height = panel.scrollHeight + 'px';
          requestAnimationFrame(function () { panel.style.height = '0px'; });
        } else {
          panel.style.height = panel.scrollHeight + 'px';
          var done = function () {
            panel.style.height = 'auto';
            panel.removeEventListener('transitionend', done);
          };
          panel.addEventListener('transitionend', done);
        }
      });
    });
    window.addEventListener('resize', function () {
      $$('.pbcl-acc__q[aria-expanded="true"]', document).forEach(function (b) {
        var p = document.getElementById(b.getAttribute('aria-controls'));
        if (p) p.style.height = 'auto';
      });
    }, { passive: true });
  }

  /* ------------------------------------------------------- chapter router */
  var VIEW_OF = {
    'pool-closing-overview': 'overview',
    'pool-closing-options': 'overview',
    'pool-closing-included': 'overview',
    'pool-closing-pricing': 'pricing',
    'pool-closing-estimate': 'pricing',
    'pool-closing-winterization': 'winterize',
    'pool-closing-upgrades': 'upgrades',
    'pool-closing-products': 'upgrades',
    'pool-closing-book': 'book',
    'pool-closing-faq': 'faq',
    /* Spare anchors so an editorial band can be dropped into a chapter other
       than Overview. Give the band section one of these as its anchor id. */
    'pool-closing-break-overview': 'overview',
    'pool-closing-break-pricing': 'pricing',
    'pool-closing-break-winterization': 'winterize',
    'pool-closing-break-upgrades': 'upgrades'
  };
  var VIEW_ORDER = ['overview', 'pricing', 'winterize', 'upgrades', 'book', 'faq'];

  function initViews(scope) {
    var nav = $('[data-pbcl-subnav]', scope);
    if (!nav) return null;
    /* Flat mode: every chapter stays on the page in reading order and the
       sub-nav only scrolls. Set from the sub-nav section's "one page" toggle.
       Nothing is hidden, so pricing, booking and the FAQ are met by anyone
       who simply keeps scrolling — and by search engines. */
    var FLAT = nav.hasAttribute('data-pbcl-flat');

    /* The outermost element the theme wraps this section in: the .pbcl
       wrapper on its own in a standalone build, the Shopify <section> around
       it on the live theme. Hiding the outer one avoids leaving the theme's
       own section padding behind as an empty gap. */
    function blockOf(node) {
      var b = node.closest ? node.closest('.pbcl') : null;
      if (!b) return null;
      var p = b.parentElement;
      if (p && p.tagName === 'SECTION' && p.className.indexOf('pb-cl-') > -1) b = p;
      return b;
    }

    var blocks = [], seen = [];
    $$('.pbcl', scope).forEach(function (w) {
      var b = blockOf(w);
      if (!b || seen.indexOf(b) > -1) return;
      seen.push(b);
      var view = 'overview', k;
      for (k in VIEW_OF) {
        if (b.querySelector('#' + k)) { view = VIEW_OF[k]; break; }
      }
      /* the sub-nav and the mobile summary sheet are chrome, present always */
      if (b.querySelector('[data-pbcl-subnav]') || b.classList.contains('pbcl-sheet-host')) view = '*';
      blocks.push({ el: b, view: view });
    });

    var current = null;

    function setView(name, opts) {
      if (VIEW_ORDER.indexOf(name) < 0) name = 'overview';
      opts = opts || {};
      if (name === current && !opts.force) return;
      current = name;

      var lead = null;
      blocks.forEach(function (b) {
        var on = FLAT || (b.view === '*' || b.view === name);
        b.el.classList.toggle('pbcl-view--off', !on);
        b.el.classList.remove('pbcl-view--lead');
        if (on && b.view !== '*' && !lead) lead = b.el;
      });
      /* the section that now opens the chapter was designed to be met
         mid-scroll, so its full top padding reads as a gap up against the
         nav. Only the leading section is trimmed. */
      if (lead && !FLAT) lead.classList.add('pbcl-view--lead');

      $$('a[href^="#"]', nav).forEach(function (a) {
        var id = a.getAttribute('href').slice(1);
        if (VIEW_OF[id]) {
          if (VIEW_OF[id] === name) a.setAttribute('aria-current', 'page');
          else a.removeAttribute('aria-current');
        } else if (name !== 'overview') {
          a.removeAttribute('aria-current');
        }
      });
      nav.setAttribute('data-view', name);

      if (opts.scroll !== false && !FLAT) {
        window.scrollTo({ top: 0, behavior: reduceMotion() ? 'auto' : 'smooth' });
      }
      window.dispatchEvent(new Event('resize'));
      document.dispatchEvent(new CustomEvent('pbcl:view', { detail: { view: name } }));
    }

    function viewForId(id) { return VIEW_OF[id] || 'overview'; }

    /* One handler for the sub-nav links and every in-page call to action. A
       target in another chapter switches to it; a target in this one scrolls. */
    function go(id, e) {
      var view = viewForId(id);
      var t = document.getElementById(id);
      if (!t && view === 'overview') return false;
      if (e) e.preventDefault();
      if (FLAT) {
        if (t) t.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
        $$('a[href^="#"]', nav).forEach(function (a) {
          if (a.getAttribute('href') === '#' + id) a.setAttribute('aria-current', 'page');
          else a.removeAttribute('aria-current');
        });
        if (history.replaceState) history.replaceState(null, '', '#' + id);
        return true;
      }
      if (view !== current) {
        setView(view, { scroll: false });
        if (view === 'overview' && t) {
          t.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, behavior: 'auto' });
          if (t) setTimeout(function () { t.scrollIntoView({ block: 'start' }); }, 40);
        }
      } else if (t) {
        t.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
      }
      if (history.replaceState) history.replaceState(null, '', '#' + id);
      return true;
    }

    $$('a[href^="#pool-closing-"]', scope).forEach(function (a) {
      a.addEventListener('click', function (e) { go(a.getAttribute('href').slice(1), e); });
    });
    window.addEventListener('hashchange', function () {
      var id = location.hash.slice(1);
      if (id) setView(viewForId(id), { scroll: false });
    });

    setView(viewForId(location.hash.slice(1)), { scroll: false, force: true });
    if (location.hash) {
      var t0 = document.getElementById(location.hash.slice(1));
      if (t0) setTimeout(function () { t0.scrollIntoView({ block: 'start' }); }, 60);
    }
    return { go: go, setView: setView, viewForId: viewForId };
  }

  /* The sticky sub-nav, made to actually stick.

     `position:sticky` is offset inside its CONTAINING BLOCK, which here is the
     one-nav-tall wrapper Shopify puts each section in — so it has nowhere to
     travel and scrolls away with its own section. And plain `position:fixed`
     is a lie inside a transformed ancestor, which Debut's page container is.
     So: lift the nav's styled .pbcl wrapper to <body> (it must be the styled
     wrapper, or every custom property the colours depend on resolves to
     nothing), leave a spacer in its slot, and track that spacer. Above the
     slot the nav sits in its place; past it, it holds the top — which is what
     sticky was meant to do. The CSS keeps `position:sticky` as the no-JS
     floor. */
  function liftSubnav() {
    var nav = $('[data-pbcl-subnav]');
    if (!nav || !document.body) return;
    var wrap = nav.closest ? nav.closest('.pbcl') : null;
    if (!wrap || wrap.classList.contains('pbcl-nav-host')) return;

    var spacer = document.createElement('div');
    spacer.className = 'pbcl-nav-spacer';
    wrap.parentNode.insertBefore(spacer, wrap);
    wrap.classList.add('pbcl-nav-host');
    document.body.appendChild(wrap);

    /* If the host theme keeps its own header pinned at the top, sit under it
       rather than on top of it. Measured, not assumed, so a theme change or a
       promo bar cannot push the two into each other. */
    function hostOffset() {
      var sel = 'header, .site-header, .shopify-section-header, #shopify-section-header, [data-section-type="header"]';
      var best = 0;
      $$(sel).forEach(function (n) {
        if (n.closest && n.closest('.pbcl')) return;
        var cs = window.getComputedStyle(n);
        if (cs.position !== 'fixed' && cs.position !== 'sticky') return;
        var r = n.getBoundingClientRect();
        if (r.height > 0 && r.top <= 1) best = Math.max(best, r.bottom);
      });
      return best;
    }

    var ticking = false;
    function paint() {
      ticking = false;
      var h = nav.offsetHeight || 58;
      if (spacer.offsetHeight !== h) spacer.style.height = h + 'px';
      var off = hostOffset();
      var t = spacer.getBoundingClientRect().top;
      wrap.style.transform = 'translateY(' + Math.max(off, Math.round(t)) + 'px)';
      /* Anchored sections must land below the pinned nav, not under it. */
      document.documentElement.style.setProperty('--pbcl-anchor-offset', (h + off + 12) + 'px');
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(paint);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    document.addEventListener('pbcl:view', function () { setTimeout(paint, 30); });
    paint();
  }

  function initSubnav(scope) {
    var nav = $('[data-pbcl-subnav]', scope);
    if (!nav || !('IntersectionObserver' in window)) return;
    var links = $$('a[href^="#"]', nav), map = {};
    links.forEach(function (a) {
      var t = document.getElementById(a.getAttribute('href').slice(1));
      if (t) map[t.id] = a;
    });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          links.forEach(function (a) { a.removeAttribute('aria-current'); });
          if (map[e.target.id]) map[e.target.id].setAttribute('aria-current', 'true');
        }
      });
    }, { rootMargin: '-45% 0px -50% 0px' });
    Object.keys(map).forEach(function (id) { io.observe(document.getElementById(id)); });
  }

  /* Buttons that behave like sub-nav links. Anchors are handled above. */
  function initJumps(scope, router) {
    $$('[data-pbcl-jump]', scope).forEach(function (b) {
      if (b.tagName === 'A') return;
      b.addEventListener('click', function (e) {
        var id = b.getAttribute('data-pbcl-jump');
        if (router && router.go(id, e)) return;
        var t = document.getElementById(id);
        if (!t) return;
        e.preventDefault();
        t.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
      });
    });
  }

  /* The pricing matrix highlights the row for whichever area you choose, in
     both the table and the card view, from one control. */
  function initAreaTable(scope) {
    var sel = $('[data-pbcl-areaselect]', scope);
    function apply(id) {
      $$('[data-area]', document).forEach(function (r) {
        r.setAttribute('data-on', r.getAttribute('data-area') === id ? '1' : '0');
      });
      var out = $('[data-pbcl-areaout]', document);
      if (out) out.hidden = !id;
      if (!id) return;
      var row = $('[data-area="' + id + '"]');
      if (!row) return;
      $$('[data-pbcl-areafig]', document).forEach(function (n) {
        var key = n.getAttribute('data-pbcl-areafig');
        var cell = row.querySelector('[data-cover="' + key + '"]');
        n.textContent = cell ? cell.textContent.trim() : '—';
      });
      var name = $('[data-pbcl-areaname]', document);
      if (name) name.textContent = row.getAttribute('data-areaname') || '';
    }
    if (sel) {
      sel.addEventListener('change', function () { apply(sel.value); });
      if (sel.value) apply(sel.value);
    }
    document.addEventListener('pbcl:area', function (e) {
      if (sel && e.detail) { sel.value = e.detail; apply(e.detail); }
    });
  }

  /* The running-total sheet is position:fixed, and `fixed` is a lie inside a
     transformed ancestor — Debut transforms its page container, which would
     size the sheet against the whole document instead of the viewport. Moving
     it to <body> before anything measures it fixes that. It has to move inside
     a .pbcl wrapper of its own or every custom property the colours depend on
     resolves to nothing; and once it is out of the estimator's block, the
     chapter router stops treating that block as page chrome. */
  function liftSheet() {
    var sheet = $('[data-pbcl-sheet]');
    if (!sheet || !document.body) return;
    if (sheet.parentElement && sheet.parentElement.classList.contains('pbcl-sheet-host')) return;
    var host = document.createElement('div');
    host.className = 'pbcl pbcl-sheet-host';
    host.appendChild(sheet);
    document.body.appendChild(host);
  }

  function initSheet(scope) {
    var sheet = $('[data-pbcl-sheet]', scope);
    if (!sheet) return;
    var hd = $('.pbcl-sheet__hd', sheet);
    if (!hd) return;
    hd.addEventListener('click', function () {
      var open = sheet.getAttribute('data-open') === '1';
      sheet.setAttribute('data-open', open ? '0' : '1');
      hd.setAttribute('aria-expanded', String(!open));
    });
  }

  /* The QR is generated from whatever the booking URL setting says, so the
     code and the button can never point at different places. */
  function initQR(scope) {
    $$('[data-pbcl-qr]', scope).forEach(function (holder) {
      var url = holder.getAttribute('data-url');
      if (!url) return;
      var node = QR.svg(url, { label: holder.getAttribute('data-label') || 'QR code linking to the Pool Busters booking portal' });
      if (!node) return;
      holder.innerHTML = '';
      holder.appendChild(node);
      holder.setAttribute('data-ready', '1');
    });
  }

  /* The booking panel restates what the visitor selected before handing them
     to the portal. It fills itself from the estimator; with no estimate it
     stays on its "nothing selected yet" copy rather than showing blanks. */
  function initBookingRecap(scope) {
    var wait = $('[data-pbcl-recap-wait]', scope);
    var ready = $('[data-pbcl-recap-ready]', scope);
    var list = $('[data-pbcl-recap]', scope);
    if (!list) return;
    document.addEventListener('pbcl:quote', function (e) {
      var rows = e.detail && e.detail.recap;
      if (!rows) {
        if (wait) wait.hidden = false;
        if (ready) ready.hidden = true;
        return;
      }
      list.innerHTML = '';
      rows.forEach(function (r) {
        var li = el('li');
        li.appendChild(el('span', 'k', r[0]));
        li.appendChild(el('span', 'v', r[1]));
        list.appendChild(li);
      });
      if (wait) wait.hidden = true;
      if (ready) ready.hidden = false;
    });
  }

  /* ------------------------------------------------------------------ boot */
  function boot() {
    var scope = document;
    initReveals(scope);
    initAccordions(scope);
    initExplorers(scope);
    liftSheet();
    var router = initViews(scope);
    liftSubnav();
    window.PBCL_ROUTER = router;
    initSubnav(scope);
    initJumps(scope, router);
    initAreaTable(scope);
    initSheet(scope);
    initQR(scope);
    initBookingRecap(scope);

    var root = $(ROOT_SEL);
    if (!root) return;

    function start(DB) {
      window.PBCL = new Estimator(root, DB);
      initJumps(root, router);
    }

    /* Preview builds embed the database inline; the Shopify theme keeps it in
       its own asset file so prices can be updated without touching code. */
    var inline = $('script[data-pbcl-pricing]');
    if (inline) {
      try { start(JSON.parse(inline.textContent)); return; } catch (e) { /* fall through */ }
    }
    var url = root.getAttribute('data-pricing');
    if (!url) return;
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('pricing ' + r.status); return r.json(); })
      .then(start)
      .catch(function () {
        var p = $('[data-pbcl-panel]', root);
        if (p) {
          p.innerHTML = '';
          var d = el('div', 'pbcl-err');
          d.textContent = 'The estimator could not load just now. Please refresh the page, or call us on (289) 929-7665 and we will price your closing over the phone.';
          p.appendChild(d);
        }
      });
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  /* export the pure engine for the automated test harness */
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { Estimator: Estimator, QR: QR, cents: cents, money: money };
  }
})();
